import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Button, Box, Modal, Stack } from '@mui/material';
import OrderForm from './OrderForm';
import { api, Balance, LprOrder } from './api';
import { useTranslation } from 'react-i18next';
import { useFetch } from '../../../hooks/useFetch';
import OrderChangeForm from './OrderChangeForm';
import { usePresetDialogs } from '../../dialog';
import { formatDateTime } from '../../../tools/util';
import { useAuthStore } from '../../../store/useAuthStore3';

export function calculateCurrentBalance(balance: Balance, currentDate: Date): number {
  if (typeof balance.value === 'string') {
    balance.value = parseFloat(balance.value);
  }
  if (typeof balance.value !== 'number' || isNaN(balance.value)) 
    return 0;

  if (!balance.startDate || !balance.endDate) {
    return balance.value;
  }
  const start = new Date(balance.startDate).getTime();
  const end = new Date(balance.endDate).getTime();
  const now = currentDate.getTime();

  if (now < start) {
    return balance.value;
  }
  if (now > end) {
    return 0;
  }

  const totalDuration = end - start;
  const elapsedDuration = now - start;

  const usedValue = balance.value * (elapsedDuration / totalDuration);
  const remainingBalance = balance.value - usedValue;
  
  return parseFloat(remainingBalance.toFixed(2));
}

// function formatDateTime(date: Date | undefined) {
  
//   if (!date) return '';

//   const pad = (n: number) => n.toString().padStart(2, '0');
//   return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
// }

const BalanceForm: React.FC = () => {
  const { t } = useTranslation();

  const [balance, setBalance] = useState<Balance | null>(null);
  const { customerId } = useAuthStore();
  const [currentBalance, setCurrentBalance] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const {data: lprOrders, } = useFetch<LprOrder[]>(api.getLprOrder, []);
  const today = new Date(); 
  const { showConfirm, showInfo, DialogComponent } = usePresetDialogs();

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const res = await api.getBalance();
        if (!res || res.value === undefined) {
          console.error(t('BalanceForm.NoBalanceError'));
          return;
        }
        setBalance(res);
      } catch (error) {
        console.error(t('BalanceForm.LoadingError'), error);
        setBalance(null);
      }
    };
    fetchBalance();
    
    const intervalId = setInterval(fetchBalance, 5000);
    // Очищаем интервал при размонтировании компонента
    return () => clearInterval(intervalId);
  }, [t]);

  useEffect(() => {
    if (!balance) return;
    setCurrentBalance(calculateCurrentBalance(balance, new Date()));
  }, [balance]);

  const handleReplenishClick = () => {
    setOpen(true);
  };

  const handleChangeClick = () => {
    setChangeOpen(true);
  };

  const handleCancelClick = async () => {
    if (!customerId) {
      alert('no customer');
      return;
    }

    // if (confirm('Підтвердіть дію. Буде припинена банківська підписка на послуги та деактивовані всі камери')) {
    // ВАЖНО!!! не забыть разместить {DialogComponent}
    const isSubscribed = await api.getSubscription(customerId);
    if (!isSubscribed) {
      await showInfo(t('BalanceForm.Subscription_Not_Found'), t('BalanceForm.Warning'));
      return;
    }

    const result = await showConfirm(
        t('BalanceForm.Your_banking_subscription_will_be_terminated'),
        t('BalanceForm.Confirm_Action'),
    );
    if (result) {
      if (await api.suspendService(customerId)) {
        await showInfo(t('BalanceForm.Subscription_is_stopped'), t('BalanceForm.Warning'));
      } else {
        await showInfo(t('BalanceForm.UnknownError'), t('BalanceForm.Warning'));
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handlechangeClose = () => {
    setChangeOpen(false);
  };

  const handlechangeSubmit = (order_count: number, endDate: Date ) => {
    setChangeOpen(false);
    setOpen(false);
  };

  if (!balance) return (<div>{t('BalanceForm.Loading')}</div>);

  return (
    <>

      {DialogComponent}

      <Card sx={{ minWidth: 275 }}>
        <CardContent>
          <Typography variant="h5" component="div" gutterBottom>
            {t('BalanceForm.CardTitle')}
          </Typography>
          <Typography sx={{ mb: 1.5 }} color="text.secondary">
            {t('BalanceForm.order_count')}: {lprOrders[0]?.order_count || 0}
          </Typography>
          {/* <Typography sx={{ mb: 1.5 }} color="text.secondary">
            {t('BalanceForm.AsOf')} {formatDateTime(today)}
          </Typography>
          <Typography variant="h4" component="div">
            {currentBalance.toFixed(2)} {balance.crn}
          </Typography> */}
          <Typography sx={{ mb: 1.5 }} color="text.secondary">
            {t('BalanceForm.PaidUntil')} {formatDateTime(balance.endDate)}
          </Typography>
          <Stack direction="column" spacing={2} sx={{ mb: 2, alignItems: 'stretch', maxWidth: 240 }} >
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={handleReplenishClick} color='warning' fullWidth>
                {t('BalanceForm.ReplenishButton')}
              </Button>
            </Box>
              
            {/* <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={handleChangeClick} disabled={!(balance.endDate && balance.endDate > new Date())} fullWidth>
                {t('BalanceForm.ChangeButton')}
              </Button>
            </Box> */}

            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={handleCancelClick} disabled={!(balance.endDate && balance.endDate > new Date())} fullWidth>
                {t('BalanceForm.Stop_subscription')}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
      <Modal open={open} onClose={handleClose}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          minWidth: 400,
        }}>
          <OrderForm 
            onClose={handleClose} 
            onSubmit={handlechangeSubmit}
            balance={balance}  
          />
        </Box>
      </Modal>

      <Modal open={changeOpen} onClose={handlechangeClose}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          minWidth: 400,
        }}>
          <OrderChangeForm onClose={handlechangeClose} onSubmit={handlechangeSubmit} balance={balance} />
        </Box>
      </Modal>
    </>
  );
};

export default BalanceForm;